import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import { fetchUser, fetchUserHasCard, fetchUserTransactionAggregates, refreshUserAfterGroupChange, selectEndDate, selectStartDate, selectUser, setEndDate, setStartDate, setUserGroup } from "../../model"
import UserSvg from '@/shared/assets/images/User.svg'
import { PageSubtitle } from "@/shared/ui/page-subtitle"
import { CountTransactions } from "../count-transactions"
import { SumTransactions } from "../sum-transactions"
import CalendarIcon from "@/shared/assets/images/CalendarBlank.svg"
import DatePicker from "react-datepicker"
import { useEffect, useRef } from "react"
import { selectAggregatesFirstLoad, selectUserCardFirstLoad, selectUserLoading } from "../../model/selectors"
import { TransactionsDynamic } from "../transactions-dynamic"
import { UserBadges } from "../user-badges"
import { UserInfoGrid } from "../user-info-grid"
import { Loader } from "@/shared/ui/loader"
import { getDisplayName } from "@/entities/user"
import { blockUserProgramCreation, unblockUserProgramCreation } from "@/shared/api/social-programs"
import notify from "@/shared/lib/notify"
import { getErrorMessage } from "@/shared/consts/error-codes"
import { selectCommunityInfo } from "@/entities/community"
import { ActionButton } from "@/shared/ui/action-button"
import { useUserAccrualModal } from "@/features/user-accrual"
import { useUserBroadcastModal } from "@/features/user-broadcast"
import { useChangeUserGroupModal } from "@/features/change-user-group"
import { useUpdateGroupModal } from "@/features/update-group"
import { useUserBlockModal } from "@/features/user-block"
import { useUserDeleteRestoreModal } from "@/features/user-delete-restore"
import { useFillProfileModal } from "@/features/fill-profile"
import { TargetType } from "@/shared/api/groups"
import AccrualSvg from '@/shared/assets/images/Coins.svg'
import BroadcastSvg from '@/shared/assets/images/Broadcast.svg'
import GroupsSvg from '@/shared/assets/images/UserList.svg'
import VisibilityAclSvg from '@/shared/assets/images/FileLock.svg'
import ProhibitSvg from '@/shared/assets/images/Prohibit.svg'
import CheckSvg from '@/shared/assets/images/Check.svg'
import TrashSvg from '@/shared/assets/images/Trash.svg'
import ArrowsLeftRightSvg from '@/shared/assets/images/ArrowsLeftRight.svg'
import SocialProgramSvg from '@/shared/assets/images/HandHeart.svg'
import PencilSvg from '@/shared/assets/images/Pencil.svg'

export const UserInfoTab = () => {
    const isFirstRender = useRef(true)
    const user = useAppSelector(selectUser)
    const isUserLoading = useAppSelector(selectUserLoading)
    const dispatch = useAppDispatch()
    const startDate = useAppSelector(selectStartDate)
    const endDate = useAppSelector(selectEndDate)
    const aggregatesFirstLoad = useAppSelector(selectAggregatesFirstLoad)
    const cardFirstLoad = useAppSelector(selectUserCardFirstLoad)
    const communityInfo = useAppSelector(selectCommunityInfo)

    const { openUserAccrualModal } = useUserAccrualModal()
    const { openUserBroadcastModal } = useUserBroadcastModal()
    const { openChangeUserGroupModal } = useChangeUserGroupModal()
    const { openVisibilityAclModal } = useUpdateGroupModal()
    const { openConfirmBlockModal } = useUserBlockModal()
    const { openUserDeleteRestoreModal } = useUserDeleteRestoreModal()
    const { openFillProfileModal } = useFillProfileModal()

    const refreshUser = () => dispatch(fetchUser(user.id))

    const handleDateRangeChange = (value: [Date, Date]) => {
        const [start, end] = value
        dispatch(setStartDate(start))
        dispatch(setEndDate(end))
    }

    useEffect(() => {
        if (!isFirstRender.current && user.id) {
            dispatch(fetchUserTransactionAggregates())
        }
    }, [user.id, startDate, endDate])

    useEffect(() => {
        if (user.id) {
            if (aggregatesFirstLoad) dispatch(fetchUserTransactionAggregates())
            if (cardFirstLoad) dispatch(fetchUserHasCard())
            isFirstRender.current = false
        }
    }, [user.id, aggregatesFirstLoad, cardFirstLoad])

    const handleToggleSocialPrograms = async () => {
        try {
            if (user.is_social_program_creation_blocked) {
                await unblockUserProgramCreation(user.id)
                notify.success('Создание программ разблокировано')
            } else {
                await blockUserProgramCreation(user.id)
                notify.success('Создание программ заблокировано')
            }
            refreshUser()
        } catch (err) {
            notify.error(getErrorMessage(err))
        }
    }

    return (
        <>
            {!isUserLoading &&
                <div className="flexbox">
                    <div className="box">
                        <div className="flex flex-col">
                            <div className="flex gap-[10px]">
                                <div className="w-[140px] h-[140px] text-white bg-color-dark flex items-center justify-center rounded-xl overflow-hidden">
                                    <UserSvg width={100} height={100} />
                                </div>
                                <div className="flex flex-col gap-[10px] justify-end">
                                    {user.is_deleted ? (
                                        <div className="flex gap-2 flex-wrap">
                                            <ActionButton
                                                onClick={() => openUserDeleteRestoreModal(user, 'recover', refreshUser)}
                                                icon={ArrowsLeftRightSvg}
                                                title="Восстановить"
                                            />
                                        </div>
                                    ) : (
                                        <div className="flex gap-2 flex-wrap">
                                            {user.is_blocked ? (
                                                <ActionButton
                                                    onClick={() => openConfirmBlockModal(user, 'unblock', refreshUser)}
                                                    icon={CheckSvg}
                                                    title="Разблокировать"
                                                />
                                            ) : (
                                                <ActionButton
                                                    onClick={() => openConfirmBlockModal(user, 'block', refreshUser)}
                                                    icon={ProhibitSvg}
                                                    title="Заблокировать"
                                                />
                                            )}
                                            <ActionButton
                                                onClick={() => openUserDeleteRestoreModal(user, 'delete', refreshUser)}
                                                icon={TrashSvg}
                                                title="Удалить"
                                            />
                                            <ActionButton
                                                onClick={() => openUserAccrualModal(user, refreshUser)}
                                                icon={AccrualSvg}
                                                title="Начислить бонусы"
                                            />
                                            <ActionButton
                                                onClick={() => openUserBroadcastModal(user, refreshUser)}
                                                icon={BroadcastSvg}
                                                title="Рассылка"
                                            />
                                            <ActionButton
                                                onClick={() => openChangeUserGroupModal(user, (group) => {
                                                    if (group) dispatch(setUserGroup({
                                                        userId: user.id,
                                                        groupId: group.id,
                                                        groupName: group.name
                                                    }))
                                                    dispatch(refreshUserAfterGroupChange(user.id))
                                                })}
                                                icon={GroupsSvg}
                                                title="Изменить группу"
                                            />
                                            <ActionButton
                                                onClick={() => openVisibilityAclModal({ id: user.id }, TargetType.USER, refreshUser)}
                                                icon={VisibilityAclSvg}
                                                title="ACL"
                                            />
                                            <ActionButton
                                                onClick={handleToggleSocialPrograms}
                                                icon={SocialProgramSvg}
                                                title={user.is_social_program_creation_blocked ? 'Разблокировать создание программ' : 'Заблокировать создание программ'}
                                            />
                                            {communityInfo.is_admin_profile_fill_enabled && (
                                                <ActionButton
                                                    onClick={() => openFillProfileModal(user, refreshUser)}
                                                    icon={PencilSvg}
                                                    title="Заполнить профиль"
                                                />
                                            )}
                                        </div>
                                    )}
                                    <UserBadges />
                                    <div className="text-[28px] font-bold">
                                        {getDisplayName(user)}
                                    </div>
                                </div>
                            </div>
                            <UserInfoGrid />
                        </div>
                    </div>
                    <PageSubtitle title={"статистика"}>
                        <div className="flexbox">
                            <div>
                                <DatePicker
                                    selected={startDate}
                                    startDate={startDate}
                                    onChange={handleDateRangeChange}
                                    endDate={endDate}
                                    selectsRange
                                    wrapperClassName="react-datepicker-filterlike"
                                    dateFormat={"dd.MM.yyyy"}
                                    showIcon
                                    enableTabLoop={false}
                                    popperProps={{
                                        placement: 'bottom'
                                    }}
                                    icon={(<CalendarIcon width={24} height={24}/>)}
                                />
                            </div>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-12 screen:col-span-4">
                                    <TransactionsDynamic />
                                </div>
                                <div className="col-span-6 screen:col-span-4">
                                    <CountTransactions />
                                </div>
                                <div className="col-span-6 screen:col-span-4">
                                    <SumTransactions />
                                </div>
                            </div>
                        </div>
                    </PageSubtitle>
                </div>
            }
            {isUserLoading &&
                <div className="box">
                    <Loader />
                </div>
            }
        </>
    )
}
